import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    Divider,
    InlineStack,
    Select,
    SkeletonBodyText,
    Tabs,
    Text,
    TextField,
} from "@shopify/polaris";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import agent from "../../app/api/agent";
import type { StorefrontLanguage, StorefrontTranslationEntry, StorefrontTranslations } from "../../app/models/StorefrontTranslations";
import { SettingsCard } from "./SettingsControls";

export type StorefrontTranslationsEditorStatus = {
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

type StorefrontTranslationsSettingsProps = {
    onStatusChange?: (status: StorefrontTranslationsEditorStatus) => void;
    onHeaderActionsChange?: (content: ReactNode | null) => void;
};

const snapshot = (values: Record<string, string>) => JSON.stringify(
    Object.keys(values)
        .sort((left, right) => left.localeCompare(right))
        .reduce<Record<string, string>>((accumulator, key) => {
            accumulator[key] = values[key] || "";
            return accumulator;
        }, {})
);

const entryValues = (entries: StorefrontTranslationEntry[]) =>
    entries.reduce<Record<string, string>>((accumulator, entry) => {
        accumulator[entry.key] = entry.value || "";
        return accumulator;
    }, {});

const languageLabel = (language: StorefrontLanguage) => {
    const parts = [language.name || language.locale.toUpperCase()];
    if (language.primary) parts.push("Default");
    if (!language.published) parts.push("Unpublished");
    return `${parts.join(" - ")} (${language.locale})`;
};

const groupEntries = (entries: StorefrontTranslationEntry[]) =>
    entries.reduce<Record<string, StorefrontTranslationEntry[]>>((groups, entry) => {
        groups[entry.group] = groups[entry.group] || [];
        groups[entry.group].push(entry);
        return groups;
    }, {});

const groupOrder = [
    "Vehicle Search",
    "Selector fields",
    "Product Results",
    "Fit Check",
    "Fitment Table",
    "Product Guide",
];

const selectorFieldValueOrder: Record<string, number> = {
    display_label: 1,
    placeholder: 2,
    empty_option: 3,
};

const selectorFieldFallbackId = (entry: StorefrontTranslationEntry) =>
    entry.entityId || entry.entityKey || entry.entityLabel || entry.key;

const sortTranslationGroups = (groups: [string, StorefrontTranslationEntry[]][]) =>
    groups.sort(([left], [right]) => {
        const leftIndex = groupOrder.indexOf(left);
        const rightIndex = groupOrder.indexOf(right);
        if (leftIndex !== -1 || rightIndex !== -1) {
            return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex)
                - (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex);
        }

        return left.localeCompare(right);
    });

const groupSelectorFieldEntries = (entries: StorefrontTranslationEntry[]) => {
    const groups = entries.reduce<Record<string, StorefrontTranslationEntry[]>>((accumulator, entry) => {
        const id = selectorFieldFallbackId(entry);
        accumulator[id] = accumulator[id] || [];
        accumulator[id].push(entry);
        return accumulator;
    }, {});

    return Object.entries(groups)
        .map(([id, fieldEntries]) => ({
            id,
            label: fieldEntries[0]?.entityLabel || fieldEntries[0]?.entityKey || "Selector field",
            displayOrder: fieldEntries[0]?.displayOrder ?? Number.MAX_SAFE_INTEGER,
            entries: fieldEntries.slice().sort((left, right) =>
                (selectorFieldValueOrder[left.valueType || ""] ?? 99)
                - (selectorFieldValueOrder[right.valueType || ""] ?? 99)
            ),
        }))
        .sort((left, right) => (left.displayOrder - right.displayOrder) || left.label.localeCompare(right.label));
};

// Storefront copy editor. Blank fields intentionally mean "use the built-in pack or English default".
const StorefrontTranslationsSettings = ({ onStatusChange, onHeaderActionsChange }: StorefrontTranslationsSettingsProps) => {
    const [data, setData] = useState<StorefrontTranslations>();
    const [previewData, setPreviewData] = useState<StorefrontTranslations>();
    const [selectedLocale, setSelectedLocale] = useState("");
    const [selectedPreset, setSelectedPreset] = useState("");
    const [overwritePresetText, setOverwritePresetText] = useState(false);
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [baseline, setBaseline] = useState<Record<string, string>>({});
    const [selectedGroupTab, setSelectedGroupTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [applyingPreset, setApplyingPreset] = useState(false);
    const [previewingPreset, setPreviewingPreset] = useState(false);
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const presetPreviewRequestId = useRef(0);

    const loadTranslations = useCallback(async (locale?: string) => {
        // The backend returns Shopify languages plus all editable label definitions for this locale.
        presetPreviewRequestId.current += 1;
        setLoading(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);
        setSuccess(undefined);

        try {
            const response = await agent.Settings.storefrontTranslations(locale);
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedLocale(response.selectedLocale);
            setSelectedPreset(response.selectedTerminologyPreset);
            setOverwritePresetText(false);
            setDraft(values);
            setBaseline(values);
        } catch (err: any) {
            setError(err?.message ?? "Failed to load translations.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTranslations();
    }, [loadTranslations]);

    const hasChanges = snapshot(draft) !== snapshot(baseline);
    const presetHasChanges = !!data && selectedPreset !== data.selectedTerminologyPreset;
    const displayEntries = useMemo(
        () => previewData?.entries ?? data?.entries ?? [],
        [data?.entries, previewData?.entries]
    );
    const groupedEntries = useMemo(() => groupEntries(displayEntries), [displayEntries]);
    const groups = useMemo(() => sortTranslationGroups(Object.entries(groupedEntries)), [groupedEntries]);

    const languageOptions = useMemo(
        () => (data?.languages ?? []).map((language) => ({
            label: languageLabel(language),
            value: language.locale,
        })),
        [data?.languages]
    );
    const presetOptions = useMemo(
        () => (data?.terminologyPresets ?? []).map((preset) => ({
            label: preset.label,
            value: preset.value,
        })),
        [data?.terminologyPresets]
    );

    const selectedLanguage = useMemo(
        () => (data?.languages ?? []).find((language) => language.locale === selectedLocale),
        [data?.languages, selectedLocale]
    );

    useEffect(() => {
        if (!data || !selectedLocale || !selectedPreset) {
            presetPreviewRequestId.current += 1;
            setPreviewData(undefined);
            setPreviewingPreset(false);
            return;
        }

        if (selectedPreset === data.selectedTerminologyPreset && !overwritePresetText) {
            presetPreviewRequestId.current += 1;
            setPreviewData(undefined);
            setPreviewingPreset(false);
            return;
        }

        const requestId = presetPreviewRequestId.current + 1;
        presetPreviewRequestId.current = requestId;
        setPreviewingPreset(true);

        agent.Settings.previewStorefrontTerminologyPreset({
            locale: selectedLocale,
            preset: selectedPreset,
            overwriteCustomizedText: overwritePresetText,
        })
            .then((response) => {
                if (presetPreviewRequestId.current !== requestId) return;
                setPreviewData(response);
                setError(undefined);
            })
            .catch((err: any) => {
                if (presetPreviewRequestId.current !== requestId) return;
                setPreviewData(undefined);
                setError(err?.message ?? "Failed to preview terminology preset.");
            })
            .finally(() => {
                if (presetPreviewRequestId.current === requestId) {
                    setPreviewingPreset(false);
                }
            });
    }, [data, overwritePresetText, selectedLocale, selectedPreset]);

    const handleLocaleChange = useCallback((locale: string) => {
        setSelectedLocale(locale);
        loadTranslations(locale);
    }, [loadTranslations]);

    useEffect(() => {
        setSelectedGroupTab((current) => Math.min(current, Math.max(groups.length - 1, 0)));
    }, [groups.length]);

    const updateDraftValue = useCallback((key: string, value: string) => {
        setSuccess(undefined);
        setDraft((current) => ({ ...current, [key]: value }));
    }, []);

    const displayValueForEntry = useCallback((entry: StorefrontTranslationEntry) => {
        if (previewData && !hasChanges) {
            return entry.value || "";
        }

        return draft[entry.key] ?? "";
    }, [draft, hasChanges, previewData]);

    const discardChanges = useCallback(() => {
        setDraft(baseline);
        setSuccess(undefined);
    }, [baseline]);

    const saveTranslations = useCallback(async () => {
        if (!selectedLocale) return;

        // Blank values are sent too; the API interprets them as delete override.
        presetPreviewRequestId.current += 1;
        setSaving(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);
        setSuccess(undefined);

        try {
            const response = await agent.Settings.updateStorefrontTranslations(selectedLocale, {
                translations: draft,
            });
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedPreset(response.selectedTerminologyPreset);
            setDraft(values);
            setBaseline(values);
            setSuccess("Translations saved.");
        } catch (err: any) {
            setError(err?.message ?? "Failed to save translations.");
        } finally {
            setSaving(false);
        }
    }, [draft, selectedLocale]);

    const applyTerminologyPreset = useCallback(async () => {
        if (!selectedLocale || !selectedPreset || hasChanges) return;

        presetPreviewRequestId.current += 1;
        setApplyingPreset(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);
        setSuccess(undefined);

        try {
            const response = await agent.Settings.updateStorefrontTerminologyPreset({
                locale: selectedLocale,
                preset: selectedPreset,
                overwriteCustomizedText: overwritePresetText,
            });
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedPreset(response.selectedTerminologyPreset);
            setOverwritePresetText(false);
            setDraft(values);
            setBaseline(values);
            setSuccess("Terminology preset applied.");
        } catch (err: any) {
            setError(err?.message ?? "Failed to apply terminology preset.");
        } finally {
            setApplyingPreset(false);
        }
    }, [hasChanges, overwritePresetText, selectedLocale, selectedPreset]);

    useEffect(() => {
        onStatusChange?.({
            hasChanges,
            loading,
            saving: saving || applyingPreset,
            save: saveTranslations,
            discard: discardChanges,
        });
    }, [applyingPreset, discardChanges, hasChanges, loading, onStatusChange, saveTranslations, saving]);

    const showLocalActions = !onStatusChange;

    const languageControls = useMemo(() => (
        <InlineStack gap="200" blockAlign="end" align="end">
            <div className="pf-storefront-text__language-select">
                <Select
                    label="Language"
                    labelHidden
                    options={languageOptions}
                    value={selectedLocale}
                    onChange={handleLocaleChange}
                    disabled={loading || saving || applyingPreset}
                />
            </div>
            {selectedLanguage && !selectedLanguage.published ? <Badge>Unpublished</Badge> : null}
            {showLocalActions ? (
                <>
                    <Button onClick={discardChanges} disabled={!hasChanges || saving || applyingPreset}>
                        Discard
                    </Button>
                    <Button variant="primary" onClick={saveTranslations} loading={saving} disabled={!hasChanges || loading || applyingPreset}>
                        Save
                    </Button>
                </>
            ) : null}
        </InlineStack>
    ), [
        applyingPreset,
        discardChanges,
        handleLocaleChange,
        hasChanges,
        languageOptions,
        loading,
        saveTranslations,
        saving,
        selectedLanguage,
        selectedLocale,
        showLocalActions,
    ]);

    const headerControls = data ? languageControls : null;
    const canApplyPreset = !!selectedLocale
        && !!selectedPreset
        && !hasChanges
        && !loading
        && !saving
        && !applyingPreset
        && !previewingPreset
        && (presetHasChanges || overwritePresetText);

    useEffect(() => {
        if (!onHeaderActionsChange) return;
        onHeaderActionsChange(headerControls);
        return () => onHeaderActionsChange(null);
    }, [headerControls, onHeaderActionsChange]);

    if (loading && !data) {
        return (
            <SettingsCard title="Translations">
                <SkeletonBodyText lines={8} />
            </SettingsCard>
        );
    }

    return (
        <BlockStack gap="300">
            {!onHeaderActionsChange && headerControls ? (
                <InlineStack align="end" blockAlign="center">
                    {headerControls}
                </InlineStack>
            ) : null}

            {data?.warning ? (
                <Banner tone="warning">
                    <Text as="p">{data.warning}</Text>
                </Banner>
            ) : null}

            {error ? (
                <Banner tone="critical">
                    <Text as="p">{error}</Text>
                </Banner>
            ) : null}

            {success ? (
                <Banner tone="success">
                    <Text as="p">{success}</Text>
                </Banner>
            ) : null}

            {data ? (
                <Card>
                    <BlockStack gap="300">
                        <BlockStack gap="100">
                            <Text as="h2" variant="headingMd">
                                Terminology preset
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                                Presets change generated text. Customized translations stay in place unless replaced.
                            </Text>
                        </BlockStack>
                        <div className="pf-storefront-text__preset-controls">
                            <div className="pf-storefront-text__preset-select">
                                <Select
                                    label="Terminology preset"
                                    labelHidden
                                    options={presetOptions}
                                    value={selectedPreset}
                                    onChange={(value) => {
                                        setSuccess(undefined);
                                        setSelectedPreset(value);
                                    }}
                                    disabled={loading || saving || applyingPreset}
                                />
                            </div>
                            <Checkbox
                                label="Replace customized text for this language"
                                checked={overwritePresetText}
                                onChange={(checked) => {
                                    setSuccess(undefined);
                                    setOverwritePresetText(checked);
                                }}
                                disabled={loading || saving || applyingPreset}
                            />
                            <div className="pf-storefront-text__preset-apply">
                                <Button
                                    variant="primary"
                                    onClick={applyTerminologyPreset}
                                    loading={applyingPreset}
                                    disabled={!canApplyPreset}
                                >
                                    Apply
                                </Button>
                            </div>
                        </div>
                        {hasChanges ? (
                            <Text as="p" variant="bodySm" tone="subdued">
                                Save or discard text changes before applying a preset.
                            </Text>
                        ) : null}
                    </BlockStack>
                </Card>
            ) : null}

            <Card padding="0">
                <Box paddingBlockStart="400" paddingInline="600">
                    <Tabs
                        tabs={groups.map(([group]) => ({
                            id: `${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-translations-tab`,
                            content: group,
                            panelID: `${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-translations-panel`,
                        }))}
                        selected={selectedGroupTab}
                        onSelect={setSelectedGroupTab}
                    />
                </Box>
                <Divider />
                <Box paddingBlockStart="500" paddingBlockEnd="500" paddingInline="600">
                    {groups[selectedGroupTab] ? (
                        groups[selectedGroupTab][0] === "Selector fields" ? (
                            <BlockStack gap="500">
                                {groupSelectorFieldEntries(groups[selectedGroupTab][1]).length > 0 ? (
                                    groupSelectorFieldEntries(groups[selectedGroupTab][1]).map((fieldGroup, index, fieldGroups) => (
                                        <BlockStack gap="400" key={fieldGroup.id}>
                                            <BlockStack gap="300">
                                                <Text as="h2" variant="headingMd">
                                                    {fieldGroup.label}
                                                </Text>
                                                {fieldGroup.entries.map((entry) => {
                                                    const value = displayValueForEntry(entry);
                                                    return (
                                                        <TextField
                                                            key={entry.key}
                                                            label={entry.label}
                                                            autoComplete="off"
                                                            value={value}
                                                            placeholder={entry.builtInValue || entry.defaultValue || "Blank"}
                                                            multiline={entry.multiline ? 3 : false}
                                                            onChange={(nextValue) => updateDraftValue(entry.key, nextValue)}
                                                            disabled={saving}
                                                        />
                                                    );
                                                })}
                                            </BlockStack>
                                            {index < fieldGroups.length - 1 ? <Divider /> : null}
                                        </BlockStack>
                                    ))
                                ) : (
                                    <Text as="p" tone="subdued">No enabled selector fields found.</Text>
                                )}
                            </BlockStack>
                        ) : (
                            <BlockStack gap="400">
                                <Text as="h2" variant="headingMd">
                                    {groups[selectedGroupTab][0]}
                                </Text>
                                {groups[selectedGroupTab][1].map((entry) => {
                                    const value = displayValueForEntry(entry);
                                    return (
                                        <TextField
                                            key={entry.key}
                                            label={entry.label}
                                            autoComplete="off"
                                            value={value}
                                            placeholder={entry.builtInValue || entry.defaultValue || "Blank"}
                                            multiline={entry.multiline ? 3 : false}
                                            onChange={(nextValue) => updateDraftValue(entry.key, nextValue)}
                                            disabled={saving}
                                        />
                                    );
                                })}
                            </BlockStack>
                        )
                    ) : (
                        <Text as="p" tone="subdued">No translation labels found.</Text>
                    )}
                </Box>
            </Card>
        </BlockStack>
    );
};

export default StorefrontTranslationsSettings;
